import { assertUnreachable, isDefined, isNotDefined, makeValuesPredicate } from '@sgme/fp';

import type { TcHedgeType } from '@/api/option/requestOptionRfs.ts';
import { parseLegId } from '@/api/option/utils.ts';
import type {
  CheckOptionParams,
  NullableHedges,
  NullableLegProperties,
} from '@/api/trade-capture/option/checkOption.ts';
import type {
  BinaryLegs,
  ChangedFields,
  CheckOptionBody,
  HedgeLeg,
  HedgeLegs,
  Leg,
  OptionBarrierToTc,
  StrategyLeg,
  StrategyLegs,
  TCPremiumType,
  TriggerLegs,
  VanillaLeg,
  VanillaLegs,
} from '@/api/trade-capture/option/request.model.ts';
import { formatStrikeWithPrecision } from '@/App/tiles/Option/utils/strikeUtils.ts';
import type {
  FxOption,
  HedgeType,
  LegId,
  OptionBarrier,
  OptionLegProperties,
  OptionProductName,
  OptionStrike,
  OptionType,
  PremiumType,
  SolvingStrike,
} from '@/store/option/state.ts';
import { removeUndefinedValues } from '@/utils/removeUndefinedValues.ts';

export function getCheckOptionBody(params: CheckOptionParams): CheckOptionBody {
  const productName = params.productName;
  const { hedgeType, currencyPair } = params.properties ?? {};

  const hedgeLegs = getHedgeLegs(params.hedges);
  const legs = getLegs(params, params.legProperties ?? [], productName, params.optionState);

  const premiumPaymentDateString = params.properties?.premiumPaymentDate ?? params.properties?.premiumPaymentDateTenor;
  const isinFine = premiumDateIsinFine(premiumPaymentDateString);
  return [
    {
      sessionId: params.tradeCapture.sessionId,
      tileId: params.tileId,
      request: {
        idVersion: params.tradeCapture.version + 1,
        changedFields: {
          currencyPair,
          hedgeType: mapToTCHedgeType(hedgeType),

          ...removeUndefinedValues({
            premiumPaymentDateString: isinFine ? undefined : premiumPaymentDateString,
            isInFine: isinFine ? true : undefined,
            markupCurrency: params.properties?.markupCurrency,
            amountReferenceString: toTcString(params.properties?.amountReference),
            settlementType: params.properties?.settlementType,
            cashSettlementCurrency: params.properties?.cashSettlementCurrency,
            isCrossed: params.properties?.isCrossed,
            fixingReference1: params.properties?.fixingReference1,
            fixingReference2: params.properties?.fixingReference2,
          } satisfies Partial<ChangedFields>),

          legs: {
            0: {
              productName: 'FxOptionMultileg',
              legs: {
                ...hedgeLegs,
                ...legs,
              },
            },
          },
        },
      },
    },
  ];
}

function getSolvingAtStrategyLevel(legProperties: NullableLegProperties[], defaultValue: string | null): Partial<Leg> {
  const solvingStrikeLeg = legProperties.find((leg) => leg.strike?.type === 'solving');
  const solvingStrike = solvingStrikeLeg?.strike as SolvingStrike | undefined;

  return isDefined(solvingStrike)
    ? removeUndefinedValues({
        premiumPaymentAmountAskString: solvingStrike === undefined ? undefined : defaultValue,
        premiumPaymentAmountBidString: solvingStrike === undefined ? undefined : defaultValue,
        [`premiumPaymentAmount${solvingStrike.way}String`]: toTcString(solvingStrike.solveValue),
      } satisfies Partial<Leg>)
    : {};
}

function getLegs(
  params: CheckOptionParams,
  legProperties: NullableLegProperties[],
  productName: OptionProductName,
  optionState?: FxOption,
): VanillaLegs | TriggerLegs | StrategyLegs | BinaryLegs {
  switch (productName) {
    case 'Trigger':
    case 'Binary':
    case 'Vanilla': {
      const legs: [number, VanillaLeg][] = legProperties.map((legProperties) => {
        const legProductName =
          legProperties.legProductName ??
          optionState?.properties.legs.find((leg) => leg.legId === legProperties.legId)?.legProductName;
        // TODO: BINARY maybe simplify. Is vanilla well named when it is in fact multileg
        const leg: VanillaLeg = {
          productName:
            legProductName === 'Trigger'
              ? 'FxoTriggerOption'
              : legProductName === 'Binary'
                ? 'FxoBinaryOption'
                : 'Vanilla',
          ...mapTcBarriers(legProperties.barrier1, legProperties.barrier2),
          ...getTcLeg(productName, legProperties.type, legProperties, params),
          ...getSolvingAtStrategyLevel([legProperties], null),
        };

        const tcLegId = legIdToTcLegId(legProperties.legId);
        return [tcLegId, leg];
      });
      return Object.fromEntries(legs) as VanillaLegs;
    }
    case 'Straddle':
    case 'RiskReversal':
    case 'Strangle': {
      const callLegProps = legProperties[0] ?? {};
      const putLegProps = legProperties[1] ?? {};
      return {
        '0': {
          productName,
          ...getSolvingAtStrategyLevel(legProperties, '0'),
          ...getTcLeg(productName, undefined, callLegProps, params),
          ...getTcLeg(productName, undefined, putLegProps, params),
          legs: {
            call: getStrategyTcLeg(productName, undefined, callLegProps),
            put: getStrategyTcLeg(productName, undefined, putLegProps),
          },
        },
      } satisfies StrategyLegs;
    }
    default:
      assertUnreachable(productName, `Product name ${productName} not handled`);
  }
}

function mapTcBarriers(
  barrier1: OptionBarrier | null | undefined,
  barrier2: OptionBarrier | null | undefined,
): Pick<VanillaLeg, 'barrier1' | 'barrier2'> {
  return {
    barrier1: mapTcBarrier(barrier1),
    barrier2: mapTcBarrier(barrier2),
  };
}

function mapTcBarrier(barrier: OptionBarrier | null | undefined): OptionBarrierToTc | null | undefined {
  
  if (isNotDefined(barrier) || Object.keys(barrier).length === 0) {
    return barrier ;
  }

  return {
    level: barrier?.level,
    type: barrier?.type,
    nature: barrier?.nature,
    startDateString: barrier?.startDate,
    endDateString: barrier?.endDate,
  };
}

function getTcLeg(
  productName: OptionProductName,
  callOrPut: OptionType | null | undefined,
  legProperties: Omit<NullableLegProperties, 'legId'>,
  params: CheckOptionParams,
): Leg {
  const notional = legProperties.notional;
  const { premiumType, cutOffMarketPlace } = params.properties ?? {};
  const isInFine = premiumDateIsinFine(legProperties.premiumPaymentDate);
  const isTypedStrategy = isTypedStrategyProduct(productName);

  return {
    ...removeUndefinedValues({
      type: productName !== 'Binary' ? callOrPut : undefined,

      side: productName === 'RiskReversal' ? undefined : legProperties.side,
      strikeString: productName !== 'Binary' ? mapStrike(productName, legProperties.strike) : undefined,

      expiryDateString: legProperties.expiryDate ?? legProperties.expiryDateTenor,

      amount1String:
        legProperties.notionalCurrencyIndex === 1 ? (isDefined(notional) ? String(notional) : null) : undefined,
      amount2String:
        legProperties.notionalCurrencyIndex === 2 ? (isDefined(notional) ? String(notional) : null) : undefined,

      negotiatedCurrency: legProperties.notionalCurrencyIndex,
      deliveryDateString: legProperties.deliveryDate ?? legProperties.deliveryDateTenor,
      cutOffMarketPlace,
      premiumPaymentDateString: isInFine
        ? undefined
        : (legProperties.premiumPaymentDate ?? legProperties.premiumPaymentDateTenor),
      isInFine: isInFine ? true : undefined,
      premiumType: getTCPremiumType(premiumType),

      premiumPaymentAmountBidString: isTypedStrategy ? undefined : toTcString(legProperties.premiumBid),
      premiumPaymentAmountAskString: isTypedStrategy ? undefined : toTcString(legProperties.premiumAsk),
      markupAmountString: isTypedStrategy ? undefined : toTcString(legProperties.markup),
      clientVolBidString: isTypedStrategy ? undefined : toTcString(legProperties.volatilityBid),
      clientVolAskString: isTypedStrategy ? undefined : toTcString(legProperties.volatilityAsk),
    } satisfies Partial<VanillaLeg>),
  };
}
function isTypedStrategyProduct(productName: OptionProductName) {
  switch (productName) {
    case 'RiskReversal':
    case 'Straddle':
    case 'Strangle':
      return true;
    case 'Vanilla':
    case 'Trigger':
    case 'Binary':
      return false;
    default:
      return assertUnreachable(productName, `Product name ${productName} not handled`);
  }
}

function getTCPremiumType(premiumType: PremiumType | undefined | null): TCPremiumType | undefined {
  if (isNotDefined(premiumType)) {
    return undefined;
  }

  switch (premiumType.premiumCategory) {
    case 'AMOUNT':
      return `AmountCurrency${premiumType.premiumCurrency}`;
    case 'PERCENT':
      return `PercentageCurrency${premiumType.premiumCurrency}`;
    case 'VOLATILITY':
      return `SmiledVolatility${premiumType.premiumCurrency}`;
    case 'PPS':
      return `Pip${premiumType.premiumCurrency === 1 ? '12' : '21'}`;
  }
}

function mapStrike(
  productName: OptionProductName,
  strike: OptionStrike | SolvingStrike | undefined | null,
): string | undefined {
  if (productName === 'Strangle' || productName === 'RiskReversal') {
    return undefined;
  }
  if (strike === null) {
    return '';
  }
  if (strike === undefined) {
    return undefined;
  }
  if (strike.type === 'solving') {
    return '';
  }

  return formatStrikeWithPrecision(strike);
}

/**
 * preserves null to unset
 */
function toTcString(value: unknown): string | null | undefined {
  return value === null ? null : value?.toString();
}

function getStrategyTcLeg(
  productName: OptionProductName,
  callOrPut: OptionType | undefined,
  legProperties: Omit<OptionLegProperties, 'legId' | 'legProductName'>,
): StrategyLeg {
  return {
    productName: 'Vanilla',
    ...removeUndefinedValues({
      type: callOrPut,
      side: productName === 'RiskReversal' ? legProperties.side : undefined,
      strikeString: mapStrategyStrike(productName, legProperties.strike),
      premiumPaymentAmountBidString: toTcString(legProperties.premiumBid),
      premiumPaymentAmountAskString: toTcString(legProperties.premiumAsk),
      markupAmountString: toTcString(legProperties.markup),
      clientVolBidString: toTcString(legProperties.volatilityBid),
      clientVolAskString: toTcString(legProperties.volatilityAsk),
    } satisfies Partial<StrategyLeg>),
  };
}

function mapStrategyStrike(
  productName: 'Vanilla' | 'Straddle' | 'Strangle' | 'RiskReversal' | 'Trigger' | 'Binary',
  strike: OptionStrike | SolvingStrike | undefined,
) {
  if (isNotDefined(strike)) {
    return undefined;
  }
  if (strike.type === 'solving') {
    return '';
  }
  if (productName === 'Strangle' || productName === 'RiskReversal') {
    return formatStrikeWithPrecision(strike);
  }
  return undefined;
}

function legIdToTcLegId(legId: LegId): number {
  const tcLegId = parseLegId(legId).legNumber;
  if (isNotDefined(tcLegId)) {
    throw new Error(`Invalid legId format, expected 'optionId/number', got: ${legId}`);
  }
  return Number(tcLegId);
}

function mapToTCHedgeType(hedgeType: HedgeType | null | undefined): TcHedgeType | null | undefined {
  if (hedgeType === 'Live') {
    return 'None';
  }

  return hedgeType;
}

function getHedgeLegs(hedges: NullableHedges[] | undefined): HedgeLegs {
  const hedgeLegs: [string, HedgeLeg][] = (hedges ?? []).map((hedge) => {
    const hedgeLegId = hedge.legId;

    const hedgeLeg: HedgeLeg = removeUndefinedValues({
      productName: 'FxOptionHedge',
      priceString: toTcHedgeString(hedge.rate),
      amountInCcy1String: hedge.notionalCurrency === 1 ? toTcHedgeString(hedge.notionalAmount) : undefined,
      amountInCcy2String: hedge.notionalCurrency === 2 ? toTcHedgeString(hedge.notionalAmount) : undefined,
    });
    return [hedgeLegId, hedgeLeg];
  });

  return Object.fromEntries(hedgeLegs);
}

/**
 * For some reason we send '' to unset hedges
 */
function toTcHedgeString(value: unknown): string | null | undefined {
  return value === null ? '' : value?.toString();
}

const premiumDateIsinFine = makeValuesPredicate(['FwdPrem', 'InFine', 'if', 'in fine', 'in-fine']);
